import React from "react";
import { useState, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import classnames from "classnames";
import styles from "./tooltip.module.scss";
import { useClickOutside } from "../componentsUtils";

export type placement =
  | "top"
  | "top-left"
  | "top-right"
  | "bottom"
  | "bottom-left"
  | "bottom-right"
  | "right"
  | "right-top"
  | "right-bottom"
  | "left"
  | "left-top"
  | "left-bottom";

export type Cloneable = Parameters<(typeof React)["cloneElement"]>[0];

export type ITooltipProps = {
  className?: string;
  "data-testid"?: string;
  delay?: number;
  placement?: placement;
  color?: "black" | "white";
  value: React.ReactNode;
  trigger?: "hover" | "click";
  disabled?: boolean;
  onVisibilityChanged?: (isVisible: boolean) => void;
  lineWrap?: boolean;
  children: Cloneable;
  design?: "default" | "analytics";
};

type Rect = { top: number; left: number; width: number; height: number };
const rectToStyles: (rect: Rect, direction: placement) => { top: number; left: number; opacity: number } = (
  { top, left, width, height },
  direction
) => {
  switch (direction) {
    case "right":
      return { top: top + height / 2, left: left + width, opacity: 0 };
    case "left":
      return { top: top + height / 2, left, opacity: 0 };
    case "bottom":
      return { top: top + height, left: left + width / 2, opacity: 0 };
    case "left-bottom":
    case "bottom-left":
      return { top: top + height, left, opacity: 0 };
    case "right-bottom":
    case "bottom-right":
      return { top: top + height, left: left + width, opacity: 0 };
    case "left-top":
    case "top-left":
      return { top, left, opacity: 0 };
    case "right-top":
    case "top-right":
      return { top, left: left + width, opacity: 0 };
    case "top":
    default:
      return { top, left: left + width / 2, opacity: 0 };
  }
};

const Elements = new WeakMap<HTMLElement, { showTooltip: (e: HTMLElement) => void; hideTooltip: () => void }>();
document.body.addEventListener("mouseenter", (e) => Elements.get(e.target as HTMLElement)?.showTooltip(e.target as HTMLElement), { capture: true });
document.body.addEventListener(
  "mouseleave",
  (e) => {
    if (Elements.has(e.target as HTMLElement) && !(e.relatedTarget as HTMLElement)?.closest?.(`.${styles.tooltip}`)) {
      Elements.get(e.target as HTMLElement)?.hideTooltip();
    }
  },
  { capture: true }
);

export const Tooltip: React.FC<ITooltipProps> = React.memo((props) => {
  const [isVisible, setVisibility] = useState(false);
  const [timeoutId, setTimeoutId] = useState<number | null>(null);
  const [style, setStyle] = useState({ top: 0, left: 0, opacity: 0 });
  const trigger = props.trigger || `hover`;
  const { delay, placement, onVisibilityChanged, value } = props;
  const handleVisibilyChanged = useCallback(
    (isVisible: boolean) => {
      setVisibility(isVisible);
      onVisibilityChanged?.(isVisible);
    },
    [onVisibilityChanged, setVisibility]
  );

  const showTooltip = useCallback(
    (element: HTMLElement) => {
      if (value === undefined || value === "" || props.disabled) {
        return;
      }
      const targetBoundingRect = element.getBoundingClientRect();
      setTimeoutId(
        setTimeout(() => {
          const newStyle = rectToStyles(targetBoundingRect, placement ?? `top`);
          setStyle({ ...newStyle, opacity: 1 });
          handleVisibilyChanged(true);
          setTimeoutId(null);
        }, delay ?? 0) as any
      );
    },
    [value, props.disabled, placement, handleVisibilyChanged, delay]
  );

  const hideTooltip = useCallback(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    setStyle({ top: 0, left: 0, opacity: 0 });
    handleVisibilyChanged(false);
  }, [setStyle, handleVisibilyChanged, timeoutId]);

  const handleClick = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>) => {
      if (trigger !== "click") {
        return;
      }
      if (isVisible) {
        hideTooltip();
      } else {
        showTooltip(e.target as HTMLElement);
      }
    },
    [hideTooltip, isVisible, showTooltip, trigger]
  );

  useClickOutside<HTMLDivElement>({
    callback: hideTooltip,
    activate: isVisible && trigger === "click",
    eventListenerOptions: false,
  });

  const handleMouseLeave = useCallback(
    (e: React.MouseEvent | MouseEvent) => {
      const relatedTarget = e.relatedTarget as HTMLElement;
      if (trigger === "hover" && !relatedTarget.closest?.(`.${styles.tooltip}`)) {
        hideTooltip();
      }
    },
    [hideTooltip, trigger]
  );

  const setRef = (element: HTMLElement) => {
    if (element) {
      // Since we reside in a system with multiple React apps
      // Using react event listeners was unreliable and caused tooltips to not disappear
      // Using native event listeners is more reliable
      Elements.set(element, { showTooltip, hideTooltip });
    }
  };

  const tooltip =
    isVisible &&
    ReactDOM.createPortal(
      <div
        onMouseLeave={handleMouseLeave}
        className={classnames(styles.tooltip, props.className, styles[props.color ?? "black"], styles[props.placement ?? "top"], {
          [styles.lineWrap]: props.lineWrap,
          [styles.analytics]: props.design === "analytics",
          [styles.noPointerEvents]: trigger === "hover",
        })}
        data-testid={props["data-testid"]}
        style={style}
      >
        {props.value}
      </div>,
      document.body
    );

  useEffect(() => {
    if (props.disabled) {
      hideTooltip();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.disabled]);

  return (
    <>
      {tooltip}
      {React.cloneElement<any>(props.children, trigger === "hover" ? { ref: setRef } : { onClick: handleClick })}
    </>
  );
});
Tooltip.displayName = "Tooltip";
